import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, LogIn, Loader2, ArrowLeft, Mail, Lock, CheckCircle } from 'lucide-react'

import { useAuthStore } from '@/core/store/authStore'

type LoginView = 'login' | 'forgot' | 'reset'

export function LoginPage() {
  const login = useAuthStore((s) => s.login)
  const isLoggingIn = useAuthStore((s) => s.isLoggingIn)
  const loginError = useAuthStore((s) => s.loginError)
  const requestReset = useAuthStore((s) => s.requestReset)
  const isRequestingReset = useAuthStore((s) => s.isRequestingReset)
  const resetRequestError = useAuthStore((s) => s.resetRequestError)
  const resetRequestSuccess = useAuthStore((s) => s.resetRequestSuccess)
  const clearResetMessages = useAuthStore((s) => s.clearResetMessages)

  const [view, setView] = useState<LoginView>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    await login(email, password)
  }

  async function handleForgotSubmit(e: FormEvent) {
    e.preventDefault()
    await requestReset(email)
  }

  function switchView(newView: LoginView) {
    setView(newView)
    clearResetMessages()
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-150 overflow-hidden">
          {/* Header banner */}
          <div className="bg-navy px-8 py-8 flex flex-col items-center gap-3">
            <img
              src="/images/logo/ub-logo-128.webp"
              alt="UBSafety"
              className="w-16 h-16 object-contain"
            />
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white tracking-tight">UBSafety</h1>
              <p className="text-sidebar-muted text-sm mt-1">Industrial Safety Dashboard</p>
            </div>
          </div>

          {/* Login Form */}
          {view === 'login' && (
            <div className="px-8 py-8">
              <h2 className="text-lg font-semibold text-navy mb-1">Sign In</h2>
              <p className="text-sm text-slate-500 mb-6">
                Enter your credentials to access the dashboard.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-navy"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase())}
                      disabled={isLoggingIn}
                      placeholder="you@ubsafety.ca"
                      className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-150 bg-white text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 transition"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-navy"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoggingIn}
                      placeholder="••••••••"
                      className="w-full h-10 pl-10 pr-10 rounded-lg border border-slate-150 bg-white text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-navy transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Forgot password link */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => switchView('forgot')}
                    className="text-sm text-primary hover:text-primary-dark font-medium"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Error message */}
                {loginError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                    <p className="text-sm text-danger">{loginError}</p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoggingIn || !email || !password}
                  className="w-full h-10 rounded-lg bg-primary text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                  {isLoggingIn ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Forgot Password Form */}
          {view === 'forgot' && (
            <div className="px-8 py-8">
              <button
                onClick={() => switchView('login')}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </button>

              <h2 className="text-lg font-semibold text-navy mb-1">Reset Password</h2>
              <p className="text-sm text-slate-500 mb-6">
                Enter your email and we'll send you instructions to reset your password.
              </p>

              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label
                    htmlFor="reset-email"
                    className="block text-sm font-medium text-navy"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      id="reset-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value.toLowerCase())}
                      disabled={isRequestingReset}
                      placeholder="you@ubsafety.ca"
                      className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-150 bg-white text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 transition"
                    />
                  </div>
                </div>

                {resetRequestError && (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                    <p className="text-sm text-danger">{resetRequestError}</p>
                  </div>
                )}

                {resetRequestSuccess && (
                  <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-green-700">{resetRequestSuccess}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isRequestingReset || !email}
                  className="w-full h-10 rounded-lg bg-primary text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                  {isRequestingReset ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send Reset Instructions
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} UBSafety — All rights reserved
        </p>
      </div>
    </div>
  )
}
